import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import Switch from "components/Switch";
import { DropdownGlobal } from "components/DropdownGlobal";
import { InputGeneral } from "components/Input";

import { getListMfaTypeAux } from "../../constants";
import { ContainerContent, SwitchWrapper, InputWrapper } from "./styles";

interface MFAConfigurationProps {
  editedValue: string;
  setEditedValue: (e: any) => void;
  fieldError?: boolean;
}

interface MfaConfig {
  IsMfaEnabled: boolean;
  DefaultMfaType: number;
  MfaCodeExpirationMinutes: number;
  AllowMultipleProvidersPerUser: boolean;
  ResendCodeCooldownSeconds: number;
}

const defaultConfig: MfaConfig = {
  IsMfaEnabled: false,
  DefaultMfaType: 1,
  MfaCodeExpirationMinutes: 15,
  AllowMultipleProvidersPerUser: false,
  ResendCodeCooldownSeconds: 60,
};

const MFAConfiguration = ({
  editedValue,
  setEditedValue,
  fieldError = false,
}: MFAConfigurationProps) => {
  const { t } = useTranslation();
  const listMfaTypeAux = getListMfaTypeAux(t);

  let initialConfig: MfaConfig = defaultConfig;
  try {
    initialConfig = editedValue
      ? { ...defaultConfig, ...JSON.parse(editedValue) }
      : defaultConfig;
  } catch {
    initialConfig = defaultConfig;
  }

  const [isMfaEnabled, setIsMfaEnabled] = useState(initialConfig.IsMfaEnabled);
  const [defaultMfaType, setDefaultMfaType] = useState(
    initialConfig.DefaultMfaType
  );
  const [mfaCodeExpirationMinutes, setMfaCodeExpirationMinutes] = useState(
    initialConfig.MfaCodeExpirationMinutes
  );
  const [allowMultipleProviders, setAllowMultipleProviders] = useState(
    initialConfig.AllowMultipleProvidersPerUser
  );
  const [resendCodeCooldownSeconds, setResendCodeCooldownSeconds] = useState(
    initialConfig.ResendCodeCooldownSeconds
  );

  const getMfaTypeName = (typeId: number) => {
    const mfaType = listMfaTypeAux.find((item) => item.id === typeId);
    return mfaType ? mfaType.name : listMfaTypeAux[0].name;
  };

  const updateConfigString = () => {
    const configObject: MfaConfig = {
      IsMfaEnabled: isMfaEnabled,
      DefaultMfaType: defaultMfaType,
      MfaCodeExpirationMinutes: mfaCodeExpirationMinutes,
      AllowMultipleProvidersPerUser: allowMultipleProviders,
      ResendCodeCooldownSeconds: resendCodeCooldownSeconds,
    };
    setEditedValue(JSON.stringify(configObject));
  };

  useEffect(() => {
    updateConfigString();
  }, [
    isMfaEnabled,
    defaultMfaType,
    mfaCodeExpirationMinutes,
    allowMultipleProviders,
    resendCodeCooldownSeconds,
  ]);

  return (
    <ContainerContent>
      <SwitchWrapper>
        <Switch
          dataTest="switch_mfa_enabled"
          onChange={() => setIsMfaEnabled(!isMfaEnabled)}
          checked={isMfaEnabled}
          label={t("mfa.enabled")}
        />
      </SwitchWrapper>
      <DropdownGlobal
        labelName={t("mfa.defaultType")}
        options={listMfaTypeAux}
        error={fieldError}
        setSelected={(e: any) => setDefaultMfaType(Number(e))}
        required={false}
        editDropdown={getMfaTypeName(initialConfig.DefaultMfaType)}
        defaultValue={getMfaTypeName(initialConfig.DefaultMfaType)}
      />
      <InputWrapper>
        <InputGeneral
          id="mfaCodeExpirationMinutes"
          label={t("mfa.codeExpirationMinutes")}
          value={String(mfaCodeExpirationMinutes)}
          onChange={(e) => setMfaCodeExpirationMinutes(Number(e) || 0)}
          required={false}
          error={fieldError && !mfaCodeExpirationMinutes}
          type="number"
          width={381}
          maxLength={10}
        />
      </InputWrapper>
      <InputWrapper>
        <InputGeneral
          id="resendCodeCooldownSeconds"
          label={t("mfa.resendCooldownSeconds")}
          value={String(resendCodeCooldownSeconds)}
          onChange={(e) => setResendCodeCooldownSeconds(Number(e) || 0)}
          required={false}
          error={fieldError && !resendCodeCooldownSeconds}
          type="number"
          width={381}
          maxLength={10}
        />
      </InputWrapper>
      <SwitchWrapper>
        <Switch
          dataTest="switch_allow_multiple_providers"
          onChange={() => setAllowMultipleProviders(!allowMultipleProviders)}
          checked={allowMultipleProviders}
          label={t("mfa.allowMultipleProviders")}
        />
      </SwitchWrapper>
    </ContainerContent>
  );
};

export default MFAConfiguration;
